import React, { useEffect, useState } from "react";
import styled from "styled-components/native";
import { useTheme } from "styled-components/native";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import { BulkRequestItemComponentTypes } from "@components/molecules/SearchResultListItem/types";
import OptionsComponentForBulkRequestEdit from "@components/molecules/BulkListItemComponents/OptionsComponentForBulkRequestEdit";
import { SpaceType } from "@components/atoms/Spacer/types";
import PriceContainer from "./PriceContainer";
import LineViewContainer from "@scenes/requests/utils/LineView";
import ImageComponent from "./ImageComponent";
import ItemDetails from "./ItemDetails";
import { Spacer, VDBText } from "@components/atoms";
import currency from "currency.js";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { ProductCategory } from "@utils/constants";
import PersonalisationSection from "./PersonalisationSection";
import ItemCommentsView from "./ItemCommentsView";
import { useOrgSettingsSlice } from "@redux/slices/orgSettingsSlice";
import { css } from "styled-components/native";
import { useUserCurrency } from "../../../hooks/getCurrency";
import { Pressable } from "react-native";
import useScreenOptionsHooks from "../../../hooks/useScreenOptionsHooks";
import { PriceModes } from "../PriceModeCard/types";
import { useEnableRequestItemFromSupplier } from "@redux/slices/globalSlice";
import { useSelector } from "react-redux";
import { handleDeepLink } from "@utils/product/productFormatters";
import { isMeleeItem } from "@utils/product/productValidators";
import { renderRequestStatus } from "@scenes/requests/utils/RequestsScreen";

const OrderCartItemComponent = ({
  mItem,
  productType,
  changeEditCommentModalIndex,
  hideBorder = false,
  setShowLoader,
  refetchCartData,
  showRemoveIcon,
  onPress,
  orderCartRequest,
  priceMode,
  isChildRequest,
  fromRequestModify = false,
  showCheckbox = false,
  showItemStatus = false,
  checkboxDisabled = false,
  checkBoxSelection,
  selectedItems,
  isBuilderItem,
  stoneItem1,
  stoneItem2,
  configDetails,
  applyMarginTop = true,
  applyMarginBottom = true,
  showRequestItemFromSupplier = false,
  deepLink,
  showRequestItemFromSupplierStone1 = false,
  deepLinkStone1,
}: BulkRequestItemComponentTypes) => {
  const theme = useTheme();
  const userInfo = useUserInfo();
  const typography = useTypographyStore();
  const userCurrency = useUserCurrency();
  const Item = orderCartRequest || isBuilderItem ? mItem : mItem?.item;
  const config_details = configDetails
    ? configDetails?.config
    : mItem?.configuration_details?.config;
  const personalization = configDetails
    ? configDetails?.personalization
    : mItem?.configuration_details?.personalization ||
      mItem?.additional_attributes?.personalization;
  const itemComments =
    typeof mItem?.additional_attributes?.item_comments === "string"
      ? mItem.additional_attributes.item_comments.trim()
      : "";
  const hasItemComments = itemComments.length > 0;
  const { enableJewelryHybridMode } = useOrgSettingsSlice();
  const isCustomApp = useSelector((state: any) => state.appState?.isCustomApp);
  const isConfiguratorItem =
    (enableJewelryHybridMode && config_details) ||
    (isBuilderItem && config_details);
  const { t } = useScreenOptionsHooks();
  const enableRequestItemFromSupplier = useEnableRequestItemFromSupplier();
  const showSupplierLink =
    priceMode === PriceModes.WHITE &&
    (isCustomApp ? enableRequestItemFromSupplier : true);

  const hasConfigData =
    config_details &&
    Object.keys(config_details).some((key) => key !== "Total Price");

  const [isItemChecked, setIsItemChecked] = useState(false);

  useEffect(() => {
    setIsItemChecked(
      selectedItems?.includes(Item?.stockItemRequestId ?? Item?.id) ?? false
    );
  }, [selectedItems]);

  const renderItemContent = (item: any, showStatus = false) => {
    const type =
      isBuilderItem && orderCartRequest ? item.stock_type : item.type;
    const isJewelry =
      type === ProductCategory.JEWELRY ||
      type === ProductCategory.LABGROWN_JEWELRY;
    const isNotJewelry = type !== ProductCategory.JEWELRY;
    return (
      <>
        <ImageComponent
          mItem={item}
          productType={productType}
          priceMode={priceMode}
          isCheckboxVisible={showCheckbox}
          checkboxDisabled={checkboxDisabled}
          checkBoxSelection={checkBoxSelection}
          ismItemChecked={isItemChecked}
          isBuilderItem={isBuilderItem}
          fromOrderRequest={orderCartRequest}
          {...(isNotJewelry &&
            isBuilderItem && {
              backGroundColor: "core-02",
              height: "59",
              borderRadius: true,
            })}
        />
        <RightContainer>
          {changeEditCommentModalIndex &&
            showRemoveIcon &&
            (isBuilderItem ? isJewelry : true) && (
              <OptionsComponentForBulkRequestEdit
                mItem={isBuilderItem ? item : mItem}
                {...(isBuilderItem && { stoneItem1: stoneItem1 })}
                {...(isBuilderItem && { stoneItem2: stoneItem2 })}
                isBuilderItem={isBuilderItem}
                changeEditCommentModalIndex={changeEditCommentModalIndex}
                orderCart={true}
                fromModifyScreen={fromRequestModify}
              />
            )}
          {((!fromRequestModify && !showRemoveIcon) ||
            (isBuilderItem ? isNotJewelry : false)) && (
            <Spacer size={SpaceType.Double} />
          )}
          {((isConfiguratorItem && !orderCartRequest) || isBuilderItem) && (
            <PriceComponent>
              <VDBText color={theme["core-05"]} type={typography["Minor-M"]}>
                {currency(item?.total_sales_price, {
                  symbol: item?.currency_symbol || userCurrency,
                  precision:
                    userInfo?.roundUpPrices() && !isMeleeItem(item) ? 0 : 2,
                }).format()}
              </VDBText>
            </PriceComponent>
          )}
          {showStatus && (
            <ItemStatusLabel>
              <VDBText
                type={typography["Minor-R"]}
                color={
                  theme[
                    renderRequestStatus(item?.status ?? "", t)
                      .colorCode as keyof typeof theme
                  ] as string
                }
              >
                {renderRequestStatus(item?.status ?? "", t).status}
              </VDBText>
            </ItemStatusLabel>
          )}
        </RightContainer>
      </>
    );
  };

  const renderHeader = () => {
    return (
      <>
        <OverLay applyBorder={isBuilderItem}>
          <FirstContainer
            onPress={() => {
              !fromRequestModify && onPress();
            }}
          >
            {renderItemContent(Item, showItemStatus)}
          </FirstContainer>
          {showRequestItemFromSupplier && showSupplierLink && (
            <>
              <RequestItemFromSupplierContainer
                onPress={() => {
                  handleDeepLink(deepLink ?? "");
                }}
              >
                <VDBText
                  type={typography["Body-M"]}
                  color={theme["link-static"]}
                >
                  {t("requestHistory.requestItemFromSupplier.description.text")}
                </VDBText>
              </RequestItemFromSupplierContainer>
              <Spacer size={SpaceType.Basic} />
            </>
          )}
        </OverLay>
        {isBuilderItem && stoneItem1 && (
          <OverLay paddingBottom={stoneItem2 ? 0 : 4}>
            <StoneContainer
              borderRadiusBottom={
                stoneItem2 ||
                (showRequestItemFromSupplierStone1 && showSupplierLink)
                  ? 0
                  : 8
              }
              onPress={onPress}
            >
              {renderItemContent(stoneItem1)}
            </StoneContainer>
            {showRequestItemFromSupplierStone1 && showSupplierLink && (
              <>
                <RequestItemFromSupplierContainer
                  onPress={() => {
                    handleDeepLink(deepLinkStone1 ?? "");
                  }}
                  showBorderBottom={true}
                >
                  <VDBText
                    type={typography["Body-M"]}
                    color={theme["link-static"]}
                  >
                    {t(
                      "requestHistory.requestItemFromSupplier.description.text"
                    )}
                  </VDBText>
                </RequestItemFromSupplierContainer>
                <Spacer size={SpaceType.Basic} />
              </>
            )}
          </OverLay>
        )}
        {isBuilderItem && stoneItem2 && (
          <>
            <OverLay paddingTop={0}>
              <LineViewContainer background={"core-03"} />
              <StoneContainer borderRadiusTop={0} onPress={onPress}>
                {renderItemContent(stoneItem2)}
              </StoneContainer>
            </OverLay>
          </>
        )}
      </>
    );
  };

  return (
    <ParentContainer
      theme={theme}
      hideBorder={hideBorder}
      applyMarginTop={applyMarginTop}
      applyMarginBottom={applyMarginBottom}
    >
      {renderHeader()}
      <Pressable onPress={onPress}>
        {hasConfigData && (
          <>
            <LineViewContainer background={"core-03"} />
            <ItemDetails configurationDetails={config_details} />
          </>
        )}
        {Object.keys?.(personalization || {})?.length > 0 && (
          <PersonalisationSection data={personalization || {}} />
        )}
        {hasItemComments && (
          <>
            <LineViewContainer background={"core-03"} />
            <ItemCommentsView value={itemComments} />
          </>
        )}
      </Pressable>
      <LineViewContainer background={"core-03"} />
      <PriceContainer
        mItem={mItem}
        userInfo={userInfo}
        setShowLoader={setShowLoader}
        refetchCartData={refetchCartData}
        orderCartRequest={orderCartRequest}
        isChildRequest={isChildRequest}
        isBuilderItem={isBuilderItem}
        {...(isBuilderItem && {
          itemPrice:
            Number(Item?.total_sales_price) +
            Number(stoneItem1?.total_sales_price) +
            Number(stoneItem2?.total_sales_price ?? 0),
        })}
        isOrderCart={true}
      />
      {!isBuilderItem && showRequestItemFromSupplier && showSupplierLink && (
        <RequestItemFromSupplierContainer
          onPress={() => {
            handleDeepLink(deepLink ?? "");
          }}
        >
          <VDBText type={typography["Body-M"]} color={theme["link-static"]}>
            {t("requestHistory.requestItemFromSupplier.description.text")}
          </VDBText>
        </RequestItemFromSupplierContainer>
      )}
    </ParentContainer>
  );
};

const ParentContainer = styled.View<{
  applyMarginTop?: boolean;
  applyMarginBottom?: boolean;
  theme: any;
  hideBorder?: boolean;
  isChecked?: boolean;
}>`
  height: auto;
  width: auto;
  border-radius: 8px;
  border-color: ${({ theme, isChecked, hideBorder }) =>
    theme[isChecked && !hideBorder ? "diam-04" : "core-04"]};
  border-width: ${({ isChecked, hideBorder }) =>
    isChecked && !hideBorder ? 2 : 1}px;
  margin-top: ${({ applyMarginTop }) =>
    applyMarginTop ? ` ${SpaceType.Basic}px` : 0};
  margin-bottom: ${({ applyMarginBottom }) =>
    applyMarginBottom ? ` ${SpaceType.Basic}px` : 0};
`;

const FirstContainer = styled.Pressable`
  width: auto;
  background-color: ${({ theme }) => theme["core-01"]};
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  flex-direction: row;
  justify-content: space-between;
`;

const OverLay = styled.View<{
  applyBorder?: boolean;
  matchingStone?: boolean;
  paddingTop?: number;
  paddingBottom?: number;
}>`
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  ${({ applyBorder, paddingBottom }) =>
    !applyBorder &&
    css`
      padding-horizontal: 8px;
      padding-bottom: ${paddingBottom ?? 4}px;
    `}
  background-color: ${({ theme }) => theme["core-01"]};
`;

const StoneContainer = styled.Pressable<{
  borderRadiusTop?: number;
  borderRadiusBottom?: number;
}>`
  width: auto;
  background-color: ${({ theme }) => theme["core-02"]};
  border-top-left-radius: ${({ borderRadiusTop }) => borderRadiusTop ?? "8"}px;
  border-top-right-radius: ${({ borderRadiusTop }) => borderRadiusTop ?? "8"}px;
  border-bottom-left-radius: ${({ borderRadiusBottom }) =>
    borderRadiusBottom ?? "8"}px;
  border-bottom-right-radius: ${({ borderRadiusBottom }) =>
    borderRadiusBottom ?? "8"}px;
  flex-direction: row;
  justify-content: space-between;
`;

const RightContainer = styled.View`
  justify-content: space-between;
  align-items: flex-end;
  padding-bottom: 3px;
`;

const PriceComponent = styled.View`
  margin-right: ${SpaceType.Basic}px;
  margin-left: ${SpaceType.Basic}px;
`;

const ItemStatusLabel = styled.View`
  margin-right: ${SpaceType.Basic}px;
  margin-bottom: ${SpaceType.ExtraSmall}px;
`;

const RequestItemFromSupplierContainer = styled.Pressable<{
  showBorderBottom: boolean;
}>`
  padding: ${SpaceType.Basic}px ${SpaceType.Double}px;
  justify-content: center;
  align-items: center;
  border-top-color: ${({ theme }) => theme["core-04"]};
  border-top-width: 1px;
  flex-direction: column;
  background-color: ${({ theme }) => theme["core-02"]};
  ${({ showBorderBottom }) =>
    showBorderBottom &&
    css`
      border-bottom-left-radius: ${SpaceType.Basic}px;
      border-bottom-right-radius: ${SpaceType.Basic}px;
    `}
`;

export default React.memo(OrderCartItemComponent, (prevProps, nextProps) => {
  return (
    prevProps.mItem === nextProps.mItem &&
    prevProps.stoneItem1 === nextProps.stoneItem1 &&
    prevProps.stoneItem2 === nextProps.stoneItem2 &&
    prevProps.productType === nextProps.productType &&
    prevProps.selectedItems === nextProps.selectedItems &&
    prevProps.checkBoxSelection === nextProps.checkBoxSelection &&
    prevProps.showItemStatus === nextProps.showItemStatus &&
    prevProps.mItem?.status === nextProps.mItem?.status
  );
});
